import { h as __h } from "preact";
import { computed, signal } from "@preact/signals";
import { Component } from "preact";
import { relayResponse } from "~/backend/validate.js";
import { query, response } from "~/lib/api.js";
import { identity } from "~/lib/identity.js";
import html from "~/util/html.js";
import { navigate } from "~/util/router.js";
import Title from "~/util/title.js";
import Button from "~/component/Button.js";
import Data from "~/component/Data.js";
import Icon from "~/component/Icon.js";
import Loader from "~/component/Loader.js";
import Poller from "~/component/Poller.js";
/** @import { TriggerProps } from "~/component/Tooltip.js"; */
import Tooltip from "~/component/Tooltip.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
import FreshKeyCallout from "~/admin/component/FreshKeyCallout.js";
/** @import { FreshKey } from "~/admin/component/FreshKeyCallout.js"; */
import ObjectPage, { ObjectPageSection } from "~/admin/component/ObjectPage.js";
import { StatsGraph } from "~/admin/component/StatsGraph.js";
import LocationChip from "~/admin/res/location/LocationChip.js";
import { deleteRelay } from "~/admin/res/relay/actions/deleteRelay.js";
import { editRelay } from "~/admin/res/relay/actions/editRelay.js";
import { regenerateKey } from "~/admin/res/relay/actions/regenerateKey.js";
import { setMaintenanceMode } from "~/admin/res/relay/actions/setMaintenanceMode.js";
import RelayTable from "~/admin/res/relay/RelayTable.js";
/** @import { orgResponse, relayStatsResponse } from "~/backend/validate.js"; */

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {orgResponse} org
 * @property {string} relayId
 */
export default class RelaysDetail extends Component {
  relayLookup = query(() => ({
    key: [`/v2/orgs/${this.props.org.id}/relays/${this.props.relayId}`],
    keepPreviousData: true
  }), {
    schema: response(relayResponse)
  });
  freshKey = signal(/** @type {FreshKey | null} */null);
  render() {
    const relay = this.relayLookup.data?.data;
    return [__h(Crumb, {}, __h("a", {
      href: "/org/" + this.props.org.id + "/relays/" + relay?.location_id
    }, relay?.location.name ?? __h(Loader, {}))), __h(Crumb, {}, relay?.name ?? __h(Loader, {})), relay && __h(Title, {
      title: relay.name
    }), __h(Data, {
      loading: this.relayLookup.loading && !this.relayLookup.data,
      error: this.relayLookup.error,
      empty: Boolean(this.relayLookup.data) && !relay
    }, relay && this.renderPage(relay))];
  }

  /** @param {relayResponse} relay */
  renderPage(relay) {
    return __h(ObjectPage, {
      backlink: {
        href: `/org/${this.props.org.id}/relays/${relay.location_id}`,
        name: relay.location.name
      },
      title: [__h(Icon, {
        name: "antenna",
        size: 22
      }), __h("span", {}, relay.name)],
      desc: relay.description,
      actions: identity.permission(this.props.org.id, "relays", "edit") && [__h(Button, {
        size: "small",
        onClick: () => editRelay({
          orgId: this.props.org.id
        }, relay)
      }, __h(Icon, {
        name: "pencil",
        size: 12
      }), "Edit relay"), __h(Button, {
        size: "small",
        onClick: async () => {
          const res = await regenerateKey({
            orgId: this.props.org.id
          }, relay);
          if (res) this.freshKey.value = res;
        }
      }, __h(Icon, {
        name: "key",
        size: 12
      }), "Regenerate key"), __h(Tooltip, {
        contents: "Prevent this relay from accepting new connections.",
        disabled: relay.is_maintenance_mode_enabled
      }, (/** @type {TriggerProps} */{
        ref,
        ...trigger
      }) => __h(Button, {
        size: "small",
        kind: relay.is_maintenance_mode_enabled ? "warning" : "neutral",
        innerRef: ref,
        ...trigger,
        onClick: () => setMaintenanceMode({
          orgId: this.props.org.id,
          enabled: !relay.is_maintenance_mode_enabled
        }, relay)
      }, __h(Icon, {
        name: "traffic-cone",
        size: 12
      }), relay.is_maintenance_mode_enabled ? "Disable maintenance mode" : "Enable maintenance mode")), __h(Button, {
        size: "small",
        kind: "negative",
        onClick: async () => {
          const res = await deleteRelay({
            orgId: this.props.org.id
          }, relay);
          if (res) navigate(`/org/${this.props.org.id}/relays/${relay.location_id}`);
        }
      }, __h(Icon, {
        name: "trash",
        size: 12
      }), "Delete relay")],
      sidebar: [__h(ObjectPageSection, {
        title: "Status"
      }, RelayTable.status(relay)), __h(ObjectPageSection, {
        title: "Version",
        empty: "Unknown"
      }, RelayTable.version(relay)), __h(ObjectPageSection, {
        title: "Location"
      }, __h(LocationChip, {
        location: {
          ...relay.location,
          org_id: this.props.org.id
        }
      })), __h(ObjectPageSection, {
        title: "Uptime"
      }, RelayTable.uptime(relay)), __h(ObjectPageSection, {
        title: "Active connections",
        empty: "Unknown"
      }, relay.stats?.active_connections), __h(ObjectPageSection, {
        title: "Private address"
      }, RelayTable.privateAddress(relay)), __h(ObjectPageSection, {
        title: "Public address"
      }, RelayTable.publicAddress(relay))]
    }, __h(FreshKeyCallout, {
      freshKey: this.freshKey.value
    }), __h(ObjectPageSection, {
      title: "Stats"
    }, __h(RelayStats, {
      orgId: this.props.org.id,
      relayId: this.props.relayId
    })));
  }
}

/**
 * @extends {Component<StatsProps>}
 *
 * @typedef {object} StatsProps
 * @property {string} orgId
 * @property {string} relayId
 */
class RelayStats extends Component {
  relayLookup = query(() => ({
    key: [`/v2/orgs/${this.props.orgId}/relays/${this.props.relayId}`]
  }), {
    schema: response(relayResponse)
  });
  rawSamples = signal(/** @type {relayStatsResponse[]} */[]);
  samples = computed(() => {
    const cpu = [];
    const mem = [];
    const disk = [];
    const clientBits = [];
    const hostBits = [];
    for (const s of this.rawSamples.value) {
      cpu.push({
        timestamp: s.reported_at,
        value: s.cpu_busy_percent ?? 0
      });
      mem.push({
        timestamp: s.reported_at,
        value: s.memory_percent ?? 0
      });
      disk.push({
        timestamp: s.reported_at,
        value: s.disk_used_percent ?? 0
      });
      clientBits.push({
        timestamp: s.reported_at,
        value: s.client_bits_per_second / 1e6
      });
      hostBits.push({
        timestamp: s.reported_at,
        value: s.host_bits_per_second / 1e6
      });
    }
    return {
      cpu,
      mem,
      disk,
      clientBits,
      hostBits
    };
  });
  #poll = async () => {
    const res = await this.relayLookup.fetch().catch(() => {});
    const stats = res?.data?.stats;
    if (!stats) return;
    if (this.rawSamples.peek().at(-1)?.reported_at === stats.reported_at) return;
    this.rawSamples.value = [...this.rawSamples.peek(), stats].slice(-StatsGraph.MAX_SAMPLES);
  };
  render() {
    const {
      cpu,
      mem,
      disk,
      clientBits,
      hostBits
    } = this.samples.value;
    return [__h(Poller, {
      poll: this.#poll,
      duration: StatsGraph.DURATION
    }), __h("div", {
      style: "display: flex; flex-direction: column; width: 100%; align-items: stretch; gap: var(--size-3);"
    }, __h(StatsGraph, {
      title: "Bitrate",
      format: RelayTable.bitrate,
      label: "Host",
      points: hostBits,
      stack: {
        label: "Client",
        points: clientBits
      }
    }), __h(StatsGraph, {
      title: "CPU usage",
      format: RelayTable.cpu,
      domain: [0, 100],
      points: cpu
    }), __h(StatsGraph, {
      title: "Memory usage",
      format: RelayTable.memory,
      domain: [0, 100],
      points: mem
    }), __h(StatsGraph, {
      title: "Disk usage",
      format: RelayTable.disk,
      domain: [0, 100],
      points: disk
    }))];
  }
}